import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { formatNumber } from './produksi';
import { formatPeriode } from './EstateDashboardComponent';
import { TooltipProps } from 'recharts';

export const CustomTooltipTon: React.FC<TooltipProps<any, any>> = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
        <Box bg="white" boxShadow="md" p={2} borderRadius="md">
            <Text fontSize="sm" fontWeight="bold" mb={1}>
                {typeof label === 'string' ? formatPeriode(label) : label}
            </Text>
            {payload.map(entry => (
                <Flex key={entry.dataKey} align="center" mb={1}>
                    <Box w="10px" h="10px" bg={entry.color} mr={2} />
                    <Text fontSize="sm">
                        {entry.name}: {formatTooltipValue(entry.value as number, entry.name)}
                    </Text>
                </Flex>
            ))}
        </Box>
    );
};

export const CustomTooltipDaily: React.FC<TooltipProps<Record<string, any>, string>> = ({
    active,
    payload,
    label,
}) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
        <Box bg="white" boxShadow="md" p={3} borderRadius="md">
            <Text fontSize="sm" fontWeight="bold" mb={1}>
                Tanggal: {label}
            </Text>
            {payload.map((entry, idx) => (
                <Flex key={`${entry.dataKey}-${idx}`} align="center" mb={1}>
                    <Box w="10px" h="10px" bg={entry.color} mr={2} />
                    <Text fontSize="sm">
                        {entry.name}: {formatTooltipValue(entry.value, entry.name)}
                    </Text>
                </Flex>
            ))}
        </Box>
    );
};

export const CustomTooltipDailyLhp: React.FC<TooltipProps<any, any>> = ({
    active,
    payload,
    label,
}) => {
    if (!active || !payload || payload.length === 0) return null;

    const { intPlusExt } = payload[0].payload;

    return (
        <Box bg="white" boxShadow="md" p={3} borderRadius="md">
            <Text fontSize="sm" fontWeight="bold" mb={1}>
                Tanggal: {label}
            </Text>
            {payload.map((entry, idx) => {
                const value = entry.value as number;
                const isThroughput = entry.name === 'Throughput';
                
                // Jangan tampilkan persen untuk TBS Olah, Jam Olah, dan Throughput
                let pctText = '';
                if (!isThroughput && entry.name !== 'TBS Olah' && entry.name !== 'Jam Olah' && intPlusExt > 0) {
                    const pct = ((value / intPlusExt) * 100).toFixed(1);
                    pctText = ` (${pct}%)`;
                }

                // Format value based on type
                let displayValue = formatNumber(value, 'number');
                if (isThroughput) {
                    displayValue = `${Number(value).toFixed(2)} Ton/Jam`;
                } else if (entry.name === 'Jam Olah') {
                    displayValue = `${Math.round(value as number)} Jam`;
                }

                return (
                    <Flex key={`${entry.dataKey}-${idx}`} align="center" mb={1}>
                        <Box w="10px" h="10px" bg={entry.color} mr={2} />
                        <Text fontSize="sm">
                            {entry.name}: {displayValue}{pctText}
                        </Text>
                    </Flex>
                );
            })}
        </Box>
    );
};

export const CustomTooltipCangkang: React.FC<TooltipProps<any, any>> = ({
    active,
    payload,
    label,
}) => {
    if (!active || !payload || !payload.length) return null;
    const { Stok, Produksi, Kirim, StokAkhir } = payload[0].payload;
    return (
        <Box bg="white" boxShadow="md" p={2} borderRadius="md">
        <Text fontSize="sm" fontWeight="bold" mb={2}>
            {formatPeriode(label)}
        </Text>
        {payload.map((entry) => (
            <Flex key={entry.dataKey} align="center" mb={1}>
            <Box w="10px" h="10px" bg={entry.color} mr={2} />
            <Text fontSize="sm">
                {entry.name}: {formatNumber(entry.value as number, 'number')}
            </Text>
            </Flex>
        ))}
        {/* <Flex align="center" mt={1}>
            <Box w="10px" h="10px" bg="#000" mr={2} />
            <Text fontSize="sm">
            Stock Akhir: {formatNumber(StokAkhir as number, 'number')}
            </Text>
        </Flex> */}
        </Box>
    );
};

export const CustomTooltipLhp: React.FC<TooltipProps<any, any>> = ({
    active,
    payload,
    label,
}) => {
    if (!active || !payload || !payload.length) return null;
    const { IntPlusExt } = payload[0].payload;
    return (
        <Box bg="white" boxShadow="md" p={2} borderRadius="md">
            <Text fontSize="sm" fontWeight="bold" mb={2}>
                {formatPeriode(label)}
            </Text>
            {payload.map(entry => {
                const value = entry.value as number;
                const pct = IntPlusExt > 0
                    ? ((value / IntPlusExt) * 100).toFixed(1)
                    : '0.0';
                
                const pctText = entry.name === 'TBS Olah' ? '' : ` (${pct}%)`;
                
                return (
                    <Flex key={entry.dataKey} align="center" mb={1}>
                        <Box w="10px" h="10px" bg={entry.color} mr={2} />
                        <Text fontSize="sm">
                            {entry.name}: {formatNumber(value, 'number')}{pctText}
                        </Text>
                    </Flex>
                );
            })}
        </Box>
    );
};

function formatTooltipValue(value: any, name: string | undefined): string {
    const safeName = name || '';
    if (safeName === 'OER') {
        return `${(value as number).toFixed(2)}%`;
    }
    else if (safeName === 'KER') {
        return `${(value as number).toFixed(2)}%`;
    }
    else if (safeName === 'Jam Olah') {
        return `${value} Jam`;
    }
    // default ton
    return formatNumber(value as number, 'number');
}

export const CustomTooltipCostHk: React.FC<TooltipProps<any, any> & { isBlock?: boolean }> = ({ active, payload, label, isBlock }) => {
    if (!active || !payload || payload.length === 0) return null;
    const point = payload[0].payload;
    return (
        <Box bg="white" boxShadow="md" p={3} borderRadius="md">
        <Text fontSize="sm" fontWeight="bold" mb={2}>
            {isBlock ? 'Blok' : 'Divisi'}: {point.division}
        </Text>
        <Flex align="center" mb={1}>
            <Box w="10px" h="10px" bg="#8884d8" mr={2} />
            <Text fontSize="sm">
            Output per HK: {formatNumber(point.meanOutput, 'number')}
            </Text>
        </Flex>
        <Flex align="center">
            <Box w="10px" h="10px" bg="#ff0000" mr={2} />
            <Text fontSize="sm">
            Biaya per kg: {formatNumber(point.meanCost, 'number')}
            </Text>
        </Flex>
        </Box>
    );
};